import axios from "axios";
import UrlFactory from "./url_factory";
import {getAuthCookie} from "../utils/cookies";

/**
 * Ceiling for the per-user sync-status request. Past it axios aborts the
 * request (not just the wait) and, with `clarifyTimeoutError`, rejects with
 * `code: 'ETIMEDOUT'` — distinct from a plain abort (`ECONNABORTED`, e.g. a
 * navigation) — which `DataController.fetchUserSyncStatus` classifies.
 */
export const SYNC_STATUS_TIMEOUT_MS = 20000;

class API extends UrlFactory {
    static _instance = null;

    static Instance() {
        if (!API._instance) API._instance = new API();
        return API._instance;
    }

    async makeHeaders() {
        const token = getAuthCookie();
        if (token === "expired") return {};
        return {headers: {token: token}};
    }

    // POST: /api/user/register
    async requestRegisterUser(payload) {
        axios
            .post(this.createRegisterUrl(), payload)
            .then(function (response) {
                return response.data;
            })
            .catch(function (error) {
                if (error.response) return error.response.status;
            });
    }

    // POST: /api/user/authenticate
    async requestAuthenticate(payload) {
        const response = await axios.post(this.createAuthenticateUrl(), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/user
    async requestUser(token) {
        const response = await axios.get(this.createUserUrl(), await this.makeHeaders());
        return response;
    }

    // GET: /api/report/:aba
    async requestReport(aba, start, end, grouping, timeFrame) {
        const response = await axios.get(this.createReportUrl(aba, start, end, grouping, timeFrame), await this.makeHeaders());
        return response;
    }

    // GET: /api/consumer/fi/:aba
    async requestFI(aba) {
        const response = await axios.get(this.createFIUrl(aba), await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/:aba/:client_id/sso/session
    async requestSsoSession(aba, clientId) {
        const response = await axios.post(this.createSsoSessionUrl(aba, clientId), {}, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/:aba/:client_id/sso/events
    async requestSsoEvent(aba, clientId, body) {
        const response = await axios.post(this.createSsoEventsUrl(aba, clientId), body, await this.makeHeaders());
        return response;
    }

    // GET: /api/users/fi/:aba
    async requestUsers(aba, filters = {}) {
        const params = {};

        // Add date filters if they exist
        if (filters.start) params.start = filters.start;
        if (filters.end) params.end = filters.end;

        // Add other filters
        if (filters.enrollment) params.enrollment = filters.enrollment;
        if (filters.hasCompleteDDSwitch) params.hasCompleteDDSwitch = filters.hasCompleteDDSwitch === "complete";
        if (filters.stage) params.stage = filters.stage;
        if (filters.payrollStatus) params.payrollStatus = filters.payrollStatus;
        // Reports KPI card filter (Users tab only): the service returns exactly
        // the population behind that card (`funnelStage` ≠ `stage`).
        if (filters.funnelStage) params.funnelStage = filters.funnelStage;
        if (filters.sortBy) {
            params.sortBy = filters.sortBy;
            if (filters.sortDir) params.sortDir = filters.sortDir;
        }
        if (filters.page) params.page = filters.page;
        if (filters.pageSize) params.pageSize = filters.pageSize;
        if (filters.search) params.search = filters.search;
        const response = await axios.get(this.createUsersUrl(aba), {
            ...(await this.makeHeaders()),
            params // This will add the filters as query parameters
        });
        return response;
    }

    // GET: /api/admin/users/:aba
    async requestAdminUsers(aba, params = {}) {
        const response = await axios.get(this.createAdminUsersUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/consumer/ledger/:aba
    async requestLedger(aba, filters = {}) {
        const params = {};
        if (filters.start) params.start = filters.start;
        if (filters.end) params.end = filters.end;
        if (filters.page) params.page = filters.page;
        if (filters.pageSize) params.pageSize = filters.pageSize;
        if (filters.search) params.search = filters.search;
        // The Users stage filter reaches the Transactions cohort (KAN-557).
        // The other user-attribute params this used to forward (enrollment /
        // hasCompleteDDSwitch / status / payroll) are @deprecated on the
        // service (routes/ledger/fetchledger.js) and deliberately not sent.
        if (filters.stage) params.stage = filters.stage;
        if (filters.clientId) params.clientId = filters.clientId;
        // Server-side sorting and type filtering. The table is paginated by the
        // backend, so neither can be done in the browser. Omitted when the
        // caller does not set them, which is the server's own default.
        if (filters.sortBy) params.sortBy = filters.sortBy;
        if (filters.sortDir) params.sortDir = filters.sortDir;
        if (filters.type) params.type = filters.type;
        // Transaction-native filters (KAN-576). `advanceType` implies advance
        // rows; `reconciliation` is only sent when it narrows (exclude | only).
        if (filters.advanceType) params.advanceType = filters.advanceType;
        if (filters.reconciliation) params.reconciliation = filters.reconciliation;
        // Ledger-type denylist. `!== undefined`, not a truthiness check: the
        // value that matters here is the EMPTY string. The service hides
        // `service_fee` rows unless the caller says otherwise, and `exclude=`
        // is how a caller opts back in — a `if (filters.exclude)` guard would
        // drop exactly the value that does the work.
        if (filters.exclude !== undefined) params.exclude = filters.exclude;
        const response = await axios.get(this.createLedgerUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/reports/enrollment-funnel/:aba
    async requestEnrollmentFunnel(aba, filters = {}) {
        const params = {};
        if (filters.start) params.start = filters.start;
        if (filters.end) params.end = filters.end;
        if (filters.period && !filters.start && !filters.end) params.period = filters.period;
        const response = await axios.get(this.createEnrollmentFunnelUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // POST: /api/admin/reports/:aba/export
    // The service answers with an .xlsx body (200), a JSON job (202), or no
    // body (204). `responseType: "blob"` keeps the bytes intact; the caller
    // reads the JSON variants back out of the Blob (see DataController).
    async requestReportsExport(aba, body) {
        const response = await axios.post(this.createReportsExportUrl(aba), body, {
            ...(await this.makeHeaders()),
            responseType: "blob"
        });
        return response;
    }

    // GET: /api/admin/reports/:aba/export/:job_id
    async requestReportsExportJob(aba, jobId) {
        const response = await axios.get(this.createReportsExportJobUrl(aba, jobId), {
            ...(await this.makeHeaders()),
            // A poll that never settles must not pin the poller's in-flight gate.
            timeout: 15000
        });
        return response;
    }

    // POST: /api/consumer/fi/:aba
    async requestUpdateFI(aba, payload) {
        const response = await axios.post(this.createFIUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/report/ytd/:aba
    async requestYTDReport(aba) {
        const response = await axios.get(this.createYTDReportUrl(aba), await this.makeHeaders());
        return response;
    }

    // GET: /api/consumer/user/:clientId
    async requestConsumerUser(clientId) {
        const response = await axios.get(this.createConsumerUserUrl(clientId), await this.makeHeaders());
        return response;
    }

    // GET: /api/reconciliation/:aba
    async requestReconciliation(aba) {
        const response = await axios.get(this.createReconciliationUrl(aba), await this.makeHeaders());
        return response;
    }

    // GET: /api/treasury/funding/:aba
    async requestTreasuryFunding(aba) {
        const response = await axios.get(this.createTreasuryFundingUrl(aba), {
            ...(await this.makeHeaders())
        });
        return response;
    }

    // GET: /api/treasury/kpis/:aba
    async requestTreasuryKPIs(aba, startDate, endDate, timeFrame) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        if (timeFrame) params.timeFrame = timeFrame;
        const response = await axios.get(this.createTreasuryKPIsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/receivables/:aba
    async requestTreasuryReceivables(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createTreasuryReceivablesUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/activities/:aba
    async requestTreasuryActivities(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createTreasuryActivitiesUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/advance-gl/:aba (Cashflow Management — Advance GL data)
    async requestAdvanceGL(aba, params = {}) {
        const response = await axios.get(this.createTreasuryAdvanceGLUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/fee-gl/:aba (Cashflow Management — Fee GL data)
    async requestFeeGL(aba, params = {}) {
        const response = await axios.get(this.createTreasuryFeeGLUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/transactions/:aba (Cashflow Management — paginated transactions)
    async requestTransactionHistory(aba, params = {}) {
        const response = await axios.get(this.createTreasuryTransactionsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/dashboard/enrollment/:aba
    async requestDashboardEnrollment(aba, params = {}, signal) {
        const response = await axios.get(this.createDashboardEnrollmentUrl(aba), {
            ...(await this.makeHeaders()),
            params,
            signal
        });
        return response;
    }

    // GET: /api/dashboard/enrollment/:aba/user-summary
    async requestDashboardUserSummary(aba, params = {}) {
        const response = await axios.get(this.createDashboardUserSummaryUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/dashboard/usage/:aba/transactions
    async requestDashboardTransactionSummary(aba, params = {}) {
        const response = await axios.get(this.createDashboardTransactionSummaryUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/dashboard/usage/:aba/session-events
    async requestDashboardSessionEvents(aba, params = {}) {
        const response = await axios.get(this.createDashboardSessionEventsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/dashboard/enrollment/:aba/funnel
    async requestDashboardFunnel(aba, signal) {
        const response = await axios.get(this.createDashboardFunnelUrl(aba), {
            ...(await this.makeHeaders()),
            signal
        });
        return response;
    }

    // GET: /api/dashboard/enrollment/:aba/cohort-users
    async requestDashboardCohortUsers(aba, params = {}) {
        const response = await axios.get(this.createDashboardCohortUsersUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/dashboard/usage/:aba
    async requestDashboardUsage(aba, params = {}, signal) {
        const response = await axios.get(this.createDashboardUsageUrl(aba), {
            ...(await this.makeHeaders()),
            params,
            signal
        });
        return response;
    }

    // GET: /api/dashboard/monetization/:aba
    async requestDashboardMonetization(aba, params = {}, signal) {
        const response = await axios.get(this.createDashboardMonetizationUrl(aba), {
            ...(await this.makeHeaders()),
            params,
            signal
        });
        return response;
    }

    // GET: /api/treasury/cashflow/:aba
    async requestTreasuryCashflow(aba, startDate, endDate) {
        const params = {};
        if (startDate && endDate) {
            params.start = startDate;
            params.end = endDate;
        }
        const response = await axios.get(this.createTreasuryCashflowUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // POST: /api/admin/users/:aba/:user_id/remove-access
    async requestRemoveUserAccess(aba, userId) {
        const response = await axios.post(this.createRemoveUserAccessUrl(aba, userId), {}, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/:aba/:user_id/change-role
    async requestChangeUserRole(aba, userId, roleId) {
        const response = await axios.post(this.createChangeUserRoleUrl(aba, userId), {role_id: roleId}, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/send-invitation
    async requestSendInvitation(payload) {
        const response = await axios.post(this.createSendInvitationUrl(), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/roles/:aba
    async requestListRoles(aba) {
        const response = await axios.get(this.createRolesUrl(aba), await this.makeHeaders());
        return response;
    }

    // POST: /api/roles/:aba
    async requestCreateRole(aba, payload) {
        const response = await axios.post(this.createRolesUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // PUT: /api/roles/:aba/:role_id
    async requestUpdateRole(aba, roleId, payload) {
        const response = await axios.put(this.createRoleDetailUrl(aba, roleId), payload, await this.makeHeaders());
        return response;
    }

    // DELETE: /api/roles/:aba/:role_id
    async requestDeleteRole(aba, roleId) {
        const response = await axios.delete(this.createRoleDetailUrl(aba, roleId), await this.makeHeaders());
        return response;
    }

    // PUT: /api/roles/:aba/:role_id/scopes
    async requestSetRoleScopes(aba, roleId, scopeIds) {
        const response = await axios.put(this.createRoleScopesUrl(aba, roleId), { scope_ids: scopeIds }, await this.makeHeaders());
        return response;
    }

    // PATCH: /api/roles/:aba/:role_id/set-default
    async requestSetDefaultRole(aba, roleId) {
        const response = await axios.patch(this.createRoleSetDefaultUrl(aba, roleId), {}, await this.makeHeaders());
        return response;
    }

    // GET: /api/roles/scopes/catalog
    async requestScopesCatalog(aba) {
        const response = await axios.get(this.createScopesCatalogUrl(aba), await this.makeHeaders());
        return response;
    }

    // GET: /api/user/validate-invitation
    async requestValidateInvitation(token) {
        const response = await axios.get(this.createValidateInvitationUrl(token), await this.makeHeaders());
        return response;
    }

    // POST: /api/user/update-password
    async requestUpdatePassword(payload) {
        const response = await axios.post(this.createUpdatePasswordUrl(), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/user/validate-password-reset
    async requestValidatePasswordReset(token) {
        const response = await axios.get(this.createValidatePasswordResetUrl(token));
        return response;
    }

    // POST: /api/user/register
    async requestRegister(payload) {
        const response = await axios.post(this.createRegisterUrl(), payload);
        return response;
    }

    // PUT: /api/user/account
    async requestUpdateAccount(payload) {
        const response = await axios.put(this.createUpdateAccountUrl(), payload, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/send-password-reset/:aba
    async requestSendPasswordReset(aba, payload) {
        const response = await axios.post(this.createSendPasswordResetUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/billing/kpis/:aba
    async requestBillingKPIs(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createBillingKPIsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/analytics/:aba/billable-users
    async requestAnalyticsBillableUsers(aba, params = {}) {
        const response = await axios.get(this.createAnalyticsBillableUsersUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/analytics/:aba/metrics
    async requestAnalyticsMetrics(aba, params = {}) {
        const response = await axios.get(this.createAnalyticsMetricsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/analytics/:aba/reviews
    async requestReviewsSummary(aba, params = {}) {
        const response = await axios.get(this.createReviewsSummaryUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    async requestReviews(aba, params = {}) {
        const response = await axios.get(this.createReviewsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    async requestReviewDetail(aba, eventId) {
        const response = await axios.get(this.createReviewDetailUrl(aba, eventId), {
            ...(await this.makeHeaders())
        });
        return response;
    }

    // GET: /api/billing/statements/:aba
    async requestBillingStatements(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createBillingStatementsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // POST: /api/billing/export/:aba
    async requestExportBillingStatements(aba, payload) {
        const response = await axios.post(this.createExportBillingStatementsUrl(aba), payload, {
            ...(await this.makeHeaders()),
            responseType: "blob"
        });
        return response;
    }

    // GET: /api/admin/sync-monitor/:aba
    async requestSyncMonitor(aba, status = null, limit = 50) {
        const response = await axios.get(this.createSyncMonitorUrl(aba, status, limit), await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/sync-monitor/:aba/notify
    async requestSendSyncNotification(aba, payload) {
        const response = await axios.post(this.createSyncNotifyUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/trouble-ticket/:ticket_id/resolve
    async requestResolveTroubleTicket(ticketId, payload) {
        const response = await axios.post(this.createTroubleTicketResolveUrl(ticketId), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/platforms/:aba
    async requestPlatforms(aba, provider = null, search = null) {
        const response = await axios.get(this.createPlatformsUrl(aba, provider, search), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/platform-outages/:aba
    async requestPlatformOutages(aba, platformId = null, provider = null, status = "active") {
        const response = await axios.get(this.createPlatformOutagesUrl(aba, platformId, provider, status), await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/platform-outage/:aba
    async requestCreatePlatformOutage(aba, payload) {
        const response = await axios.post(this.createPlatformOutageCreateUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // PUT: /api/admin/platform-outage/:aba/:outage_id
    async requestUpdatePlatformOutage(aba, outageId, payload) {
        const response = await axios.put(this.createPlatformOutageUpdateUrl(aba, outageId), payload, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/platform-outage/:aba/:outage_id/resolve
    async requestResolvePlatformOutage(aba, outageId) {
        const response = await axios.post(this.createPlatformOutageResolveUrl(aba, outageId), {}, await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/reports/:aba/sync-state-catalog
    async requestSyncStateCatalog(aba) {
        const response = await axios.get(this.createSyncStateCatalogUrl(aba), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/users/:aba/:client_id/sync-status
    async requestUserSyncStatus(aba, clientId) {
        const response = await axios.get(this.createUserSyncStatusUrl(aba, clientId), {
            ...(await this.makeHeaders()),
            timeout: SYNC_STATUS_TIMEOUT_MS,
            transitional: { clarifyTimeoutError: true },
        });
        return response;
    }

    // GET: /api/admin/users/:aba/:client_id/funnel
    async requestUserFunnel(aba, clientId) {
        const response = await axios.get(this.createUserFunnelUrl(aba, clientId), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/users/:aba/:client_id/link-attempts
    async requestUserLinkAttempts(aba, clientId) {
        const response = await axios.get(this.createUserLinkAttemptsUrl(aba, clientId), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/users/:aba/:client_id/payroll-connections
    async requestUserPayrollConnections(aba, clientId) {
        const response = await axios.get(this.createUserPayrollConnectionsUrl(aba, clientId), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/users/:aba/:client_id/payroll-connections/:account_id/events
    async requestUserPayrollConnectionEvents(aba, clientId, connectionKey, query = {}) {
        const response = await axios.get(
            this.createUserPayrollConnectionEventsUrl(aba, clientId, connectionKey, query),
            await this.makeHeaders()
        );
        return response;
    }

    // GET: /api/admin/users/:aba/:client_id/sync-history
    async requestUserSyncHistory(aba, clientId) {
        const response = await axios.get(this.createUserSyncHistoryUrl(aba, clientId), await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/:aba/:client_id/restrict
    async requestUserRestrict(aba, clientId, active = false) {
        const response = await axios.post(this.createUserRestrictUrl(aba, clientId), {active}, await this.makeHeaders());
        return response;
    }

    // DELETE: /api/admin/users/:aba/:client_id/sync/latest
    async requestUserDeleteLatestSync(aba, clientId) {
        const response = await axios.delete(this.createUserDeleteLatestSyncUrl(aba, clientId), await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/:aba/:client_id/sync/latest/complete
    async requestUserMarkLatestSyncComplete(aba, clientId) {
        const response = await axios.post(this.createUserMarkLatestSyncCompleteUrl(aba, clientId), {}, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/:aba/:client_id/reconcile
    async requestUserReconcileAccount(aba, clientId) {
        const response = await axios.post(this.createUserReconcileAccountUrl(aba, clientId), {}, await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/tickets/:aba
    async requestTickets(aba, query = {}) {
        const response = await axios.get(this.createTicketsUrl(aba, query), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/tickets/:aba/:ticketId
    async requestTicket(aba, ticketId, query = {}) {
        const response = await axios.get(this.createTicketUrl(aba, ticketId, query), await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/tickets/:aba
    async requestCreateTicket(aba, payload) {
        const response = await axios.post(this.createTicketCreateUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/tickets/:aba/from-event
    async requestCreateTicketFromEvent(aba, payload) {
        const response = await axios.post(this.createTicketFromEventUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/tickets/:aba/:ticketId/messages
    async requestCreateTicketMessage(aba, ticketId, payload) {
        const response = await axios.post(this.createTicketMessagesUrl(aba, ticketId), payload, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/tickets/:aba/:ticketId/read
    async requestMarkTicketRead(aba, ticketId) {
        const response = await axios.post(this.createTicketReadUrl(aba, ticketId), {}, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/tickets/:aba/presigned
    async requestPresignedTicketMedia(aba, payload) {
        const response = await axios.post(this.createTicketPresignedUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/jira/:aba/issues
    async requestJiraIssues(aba, query = {}) {
        const response = await axios.get(this.createJiraIssuesUrl(aba, query), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/jira/:aba/issues/:key
    async requestJiraIssue(aba, key) {
        const response = await axios.get(this.createJiraIssueUrl(aba, key), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/jira/status-map
    async requestJiraStatusMap() {
        const response = await axios.get(this.createJiraStatusMapUrl(), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/notifications/:aba
    async requestNotifications(aba, query = {}) {
        const response = await axios.get(this.createNotificationsUrl(aba, query), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/notifications/:aba/unread-count
    async requestNotificationsUnreadCount(aba) {
        const response = await axios.get(this.createNotificationsUnreadCountUrl(aba), await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/notifications/:aba/read — body { ids: [...] }
    async requestMarkNotificationsRead(aba, payload) {
        const response = await axios.post(this.createNotificationsReadUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/consumer/fi/:aba/access-list
    async requestAccessList(aba, action = null) {
        const response = await axios.get(this.createAccessListUrl(aba, action), await this.makeHeaders());
        return response;
    }

    // POST: /api/consumer/fi/:aba/access-list
    async requestCreateAccessRule(aba, payload) {
        const response = await axios.post(this.createAccessListUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // DELETE: /api/consumer/fi/:aba/access-list/:ruleId
    async requestDeleteAccessRule(aba, ruleId) {
        const response = await axios.delete(this.createAccessListRuleUrl(aba, ruleId), await this.makeHeaders());
        return response;
    }

    // POST: /api/consumer/fi/:aba/access-list/bulk-upload
    async requestBulkUploadAccessList(aba, formData) {
        const headers = await this.makeHeaders();
        const response = await axios.post(
            this.createAccessListBulkUploadUrl(aba),
            formData,
            {
                ...headers,
                headers: {
                    ...headers.headers,
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        return response;
    }

    // GET: /api/consumer/fi/:aba/access-list/bulk-upload/:batchId
    async requestAccessListBulkStatus(aba, batchId) {
        const response = await axios.get(
            this.createAccessListBulkStatusUrl(aba, batchId),
            await this.makeHeaders()
        );
        return response;
    }

    // GET: /api/consumer/fi/:aba/free-advance-promos
    async requestFreeAdvancePromos(aba) {
        const response = await axios.get(this.createFreeAdvancePromosUrl(aba), await this.makeHeaders());
        return response;
    }

    // POST: /api/consumer/fi/:aba/free-advance-promos
    async requestCreateFreeAdvancePromo(aba, payload) {
        const response = await axios.post(this.createFreeAdvancePromosUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // PATCH: /api/consumer/fi/:aba/free-advance-promos/:promoId
    async requestUpdateFreeAdvancePromo(aba, promoId, payload) {
        const response = await axios.patch(this.createFreeAdvancePromoUrl(aba, promoId), payload, await this.makeHeaders());
        return response;
    }

    // DELETE: /api/consumer/fi/:aba/free-advance-promos/:promoId
    async requestDeleteFreeAdvancePromo(aba, promoId) {
        const response = await axios.delete(this.createFreeAdvancePromoUrl(aba, promoId), await this.makeHeaders());
        return response;
    }

    // GET: /api/consumer/fi/:aba/free-advance-promos/:promoId/members
    async requestFreeAdvancePromoMembers(aba, promoId, state = null) {
        let url = this.createFreeAdvancePromoMembersUrl(aba, promoId);
        if (state) url += `?state=${state}`;
        const response = await axios.get(url, await this.makeHeaders());
        return response;
    }

    // POST: /api/consumer/fi/:aba/free-advance-promos/:promoId/members
    async requestAddFreeAdvancePromoMember(aba, promoId, memberId) {
        const response = await axios.post(
            this.createFreeAdvancePromoMembersUrl(aba, promoId),
            { member_id: memberId },
            await this.makeHeaders()
        );
        return response;
    }

    // POST: /api/consumer/fi/:aba/free-advance-promos/:promoId/members/batch
    async requestAddFreeAdvancePromoMembersBatch(aba, promoId, memberIds) {
        const response = await axios.post(
            this.createFreeAdvancePromoMembersBatchUrl(aba, promoId),
            { member_ids: memberIds },
            await this.makeHeaders()
        );
        return response;
    }

    // POST: /api/consumer/fi/:aba/free-advance-promos/:promoId/members/batch/remove
    async requestRemoveFreeAdvancePromoMembersBatch(aba, promoId, memberIds) {
        const response = await axios.post(
            this.createFreeAdvancePromoMembersBatchRemoveUrl(aba, promoId),
            { member_ids: memberIds },
            await this.makeHeaders()
        );
        return response;
    }

    // DELETE: /api/consumer/fi/:aba/free-advance-promos/:promoId/members/:memberId
    async requestRemoveFreeAdvancePromoMember(aba, promoId, memberId) {
        const response = await axios.delete(
            this.createFreeAdvancePromoMemberUrl(aba, promoId, memberId),
            await this.makeHeaders()
        );
        return response;
    }


    // POST: /api/admin/intake-simulation/:aba
    // Multipart: `mode` + `sor` (JSON string) + each file as `documents`.
    // Content-Type is intentionally NOT set — axios derives the multipart
    // boundary from the FormData instance.
    async requestSimulateIntake(aba, {mode, sor, files}, {signal, onUploadProgress} = {}) {
        const formData = new FormData();
        formData.append("mode", mode);
        formData.append("sor", JSON.stringify(sor ?? {}));
        (files || []).forEach(file => formData.append("documents", file, file.name));

        const response = await axios.post(this.createIntakeSimulationUrl(aba), formData, {
            ...(await this.makeHeaders()),
            timeout: 60000,
            signal,
            onUploadProgress
        });
        return response;
    }

    // GET: /api/admin/intake-simulation/:aba/runs/:simulation_id
    async requestSimulationRun(aba, simulationId, {signal} = {}) {
        const response = await axios.get(this.createIntakeSimulationRunUrl(aba, simulationId), {
            ...(await this.makeHeaders()),
            signal
        });
        return response;
    }

    // GET: /api/admin/intake-simulation/:aba/presets/:mode
    async requestSimulationPreset(aba, mode) {
        const response = await axios.get(this.createIntakeSimulationPresetUrl(aba, mode), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/intake-simulation/:aba/member-sor/:client_id
    // A real member's SOR in the exact authored-SOR schema the composer edits
    // (dollars) → `{sor, meta: {client_id, account_id, member_name, loaded_at, notes}}`.
    async requestMemberSor(aba, clientId) {
        const response = await axios.get(this.createIntakeSimulationMemberSorUrl(aba, clientId), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/document-intake/:aba/jobs/:client_id
    // Real manual-upload intake jobs for one member →
    // `{client_id, member_name, jobs: [...]}`.
    async requestIntakeJobs(aba, clientId, {signal} = {}) {
        const response = await axios.get(this.createDocumentIntakeJobsUrl(aba, clientId), {
            ...(await this.makeHeaders()),
            signal
        });
        return response;
    }

    // GET: /api/admin/document-intake/:aba/content/:client_id/:job_id
    // RAW BYTES with the stored Content-Type. `responseType: 'blob'` keeps
    // images/PDFs untouched — note that on errors the response body is a
    // Blob too, so callers must map by HTTP status, never by `.error`.
    async requestIntakeJobContent(aba, clientId, jobId, {signal} = {}) {
        const response = await axios.get(this.createDocumentIntakeContentUrl(aba, clientId, jobId), {
            ...(await this.makeHeaders()),
            responseType: "blob",
            signal
        });
        return response;
    }
}

export default API;
